import { privateFetch } from "../../utils/CustomFetch"
import { CONSTANT } from "../../utils/Constant"

/**
 * Obtiene el historial de actividades de un cliente con paginación.
 */
export const getClientActivityHistory = async (clientId, page = 0, size = 20) => {
    try {
        const response = await privateFetch.get(
            `maintenance/client-activity-log/client/${clientId}?page=${page}&size=${size}`,
            {
                headers: {
                    ...CONSTANT.HEADER_TYPE,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Obtiene el historial filtrado por categoría.
 */
export const getClientActivityHistoryByCategory = async (clientId, category, page = 0, size = 20) => {
    try {
        const response = await privateFetch.get(
            `maintenance/client-activity-log/client/${clientId}/category/${category}?page=${page}&size=${size}`,
            {
                headers: {
                    ...CONSTANT.HEADER_TYPE,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Obtiene el conteo de actividades por tipo.
 */
export const getClientActivityCount = async (clientId) => {
    try {
        const response = await privateFetch.get(
            `maintenance/client-activity-log/client/${clientId}/count`,
            {
                headers: {
                    ...CONSTANT.HEADER_TYPE,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Registra una nueva actividad.
 */
export const logClientActivity = async (activityData) => {
    try {
        const response = await privateFetch.post(
            'maintenance/client-activity-log',
            activityData,
            {
                headers: {
                    ...CONSTANT.HEADER_TYPE,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Registra que un usuario vio la conversación.
 */
export const logConversationViewed = async (clientId, userId, userName) => {
    try {
        const response = await privateFetch.post(
            `maintenance/client-activity-log/conversation-viewed?clientId=${clientId}&userId=${userId}&userName=${encodeURIComponent(userName)}`,
            {},
            {
                headers: {
                    ...CONSTANT.HEADER_TYPE,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Registra que un usuario envió un mensaje.
 */
export const logMessageSent = async (clientId, userId, userName) => {
    try {
        const response = await privateFetch.post(
            `maintenance/client-activity-log/message-sent?clientId=${clientId}&userId=${userId}&userName=${encodeURIComponent(userName)}`,
            {},
            {
                headers: {
                    ...CONSTANT.HEADER_TYPE,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Verifica si un usuario ha visto la conversación.
 */
export const hasUserViewedConversation = async (clientId, userId) => {
    try {
        const response = await privateFetch.get(
            `maintenance/client-activity-log/has-viewed/${clientId}/${userId}`,
            {
                headers: {
                    ...CONSTANT.HEADER_TYPE,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Registra un cambio de estado/etapa del cliente.
 */
export const logStatusChange = async (clientId, userId, userName, previousStatus, newStatus) => {
    return logClientActivity({
        clientId,
        userId,
        userName,
        actionType: 'STATUS_CHANGED',
        actionCategory: 'STATUS',
        previousValue: previousStatus,
        newValue: newStatus,
        description: `${userName} cambió el estado de '${previousStatus || 'Sin estado'}' a '${newStatus}'`
    });
};

/**
 * Registra un cambio en las etiquetas del cliente.
 */
export const logTagChange = async (clientId, userId, userName, previousTags, newTags) => {
    return logClientActivity({
        clientId,
        userId,
        userName,
        actionType: 'TAGS_CHANGED',
        actionCategory: 'TAGS',
        previousValue: previousTags,
        newValue: newTags,
        description: `${userName} asignó etiquetas: ${newTags || 'Ninguna'}`
    });
};

/**
 * Registra una actualización en la información del cliente.
 */
export const logClientInfoUpdate = async (clientId, userId, userName, fieldName, previousValue, newValue) => {
    // Build description with the new value included (similar to tags)
    let description = `${userName} actualizó ${fieldName}`;
    if (newValue) {
        description += `: ${newValue}`;
    }
    
    return logClientActivity({
        clientId,
        userId,
        userName,
        actionType: 'INFO_UPDATED',
        actionCategory: 'CLIENT_INFO',
        previousValue: previousValue || '',
        newValue: newValue || '',
        description
    });
};

/**
 * Registra que un lead fue convertido a cliente.
 */
export const logConvertedToClient = async (clientId, userId, userName) => {
    return logClientActivity({
        clientId,
        userId,
        userName,
        actionType: 'CONVERTED_TO_CLIENT',
        actionCategory: 'STATUS',
        previousValue: 'Lead',
        newValue: 'Cliente',
        description: `${userName} convirtió el lead a Cliente`
    });
};

/**
 * Registra el envío de un correo electrónico al cliente.
 */
export const logEmailSent = async (clientId, userId, userName, subject, recipientEmail) => {
    return logClientActivity({
        clientId,
        userId,
        userName,
        actionType: 'EMAIL_SENT',
        actionCategory: 'COMMUNICATION',
        previousValue: '',
        newValue: recipientEmail,
        description: `${userName} envió correo: ${subject}`
    });
};

/**
 * Registra la generación de una propuesta para el cliente.
 */
export const logProposalGenerated = async (clientId, userId, userName, proposalName) => {
    return logClientActivity({
        clientId,
        userId,
        userName,
        actionType: 'PROPOSAL_GENERATED',
        actionCategory: 'PROPOSAL',
        previousValue: '',
        newValue: proposalName,
        description: `${userName} generó propuesta: ${proposalName}`
    });
};
